import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { getZones } from "@/lib/api";
import { tr } from "@/i18n/translations";
import { euro } from "@/lib/format";

export const ZonesExplorer = () => {
  const { t, lang } = useApp();
  const [zones, setZones] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    getZones().then((z) => setZones(z.filter((x) => x.count > 0))).catch(() => {});
  }, []);

  const spans = ["lg:col-span-7", "lg:col-span-5", "lg:col-span-5", "lg:col-span-7", "lg:col-span-4", "lg:col-span-4", "lg:col-span-4"];

  return (
    <section id="zones" className="scroll-mt-24 bg-sand py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="mb-12 max-w-2xl">
          <p className="overline text-terracotta">{t("sections.zones_over")}</p>
          <h2 className="mt-3 font-serif text-4xl font-medium text-petrol lg:text-5xl">
            {t("sections.zones_title")}
          </h2>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
          {zones.map((z, i) => (
            <motion.button
              key={z.slug}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
              onClick={() => navigate(`/stays?zone=${z.slug}`)}
              data-testid="umag-zone-card"
              className={`group relative h-64 overflow-hidden rounded-2xl text-left ${spans[i % spans.length]}`}
            >
              <img
                src={z.image}
                alt={z.name}
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-petrol-dark/90 via-petrol/30 to-transparent" />
              <div className="absolute inset-0 flex flex-col justify-end p-6 text-white">
                <div className="flex items-center justify-between">
                  <p className="overline text-gold" style={{ fontSize: "0.6rem" }}>
                    {z.count} {t("sections.stays")}
                  </p>
                  <span className="translate-x-2 opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-100">
                    <ArrowUpRight size={20} />
                  </span>
                </div>
                <h3 className="mt-1 font-serif text-2xl font-semibold">{z.name}</h3>
                <p className="text-sm text-white/75">{tr(z.tag, lang)}</p>
                <div className="mt-2 flex items-center gap-3 text-xs text-white/70">
                  <span>{z.hotels} hotel · {z.apartments} app.</span>
                  {z.from_price && (
                    <span className="font-semibold text-white">
                      {t("detail.from")} {euro(z.from_price)}
                    </span>
                  )}
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </div>
    </section>
  );
};
